import * as React from 'react';
import { useState } from 'react';
import { Typography } from '@execonline-inc/execonline-ui';
import { getDefaultDateRange } from './utils';
import { T } from '../../Translations';

interface Props {
  initialStartDate?: string;
  initialEndDate?: string;
  onSubmit: (startDate: string, endDate: string) => void;
  onClose: () => void;
}

const formatDateForInput = (dateStr?: string, defaultDate?: string): string => {
  if (!dateStr) {
    return defaultDate || '';
  }
  try {
    // Ensure the date is in YYYY-MM-DD format for the input
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  } catch {
    return defaultDate || '';
  }
};

const DateRangePopover: React.FC<Props> = ({
  initialStartDate,
  initialEndDate,
  onSubmit,
  onClose,
}) => {
  const defaults = getDefaultDateRange();
  const [startDate, setStartDate] = useState(
    formatDateForInput(initialStartDate, defaults.startDate),
  );
  const [endDate, setEndDate] = useState(formatDateForInput(initialEndDate, defaults.endDate));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (startDate && endDate) {
      onSubmit(startDate, endDate);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-72">
      <Typography variant="bodySmall" fontWeight="semibold" className="text-gray-800 mb-4">
        <T kind="Adjust date range" />
      </Typography>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* From date */}
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            <T kind="From" />
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* To date */}
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            <T kind="To" />
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!startDate || !endDate}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-teal-700 text-white text-sm font-medium rounded-lg hover:bg-teal-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          data-intercom-target="ai-summary-date-range-submit-button"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>
            <T kind="Regenerate Insights" />
          </span>
        </button>
      </form>
    </div>
  );
};

export default DateRangePopover;
